import { HTMLAttributes, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import axios from "axios"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { useMutation } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { setCookie } from "nookies"
import { Link, useNavigate } from "react-router-dom"
import { LOGIN_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { toast } from "@hooks/use-toast"
import {
  INVITATIONS_ACCEPT_PAGE_ROUTE,
  LOGIN_COMPLETE_PAGE_ROUTE,
  RESET_PASSWORD_LINK_PAGE_ROUTE,
  SIGNUP_PAGE_ROUTE,
} from "@pma/utils/page-routes"
import { PasswordInput } from "@components/common/password-input"
import { cn } from "@pma/utils/helpers"
import { Card } from "@components/common/card"
import Recaptcha from "./recaptcha"
import GoogleSignIn from "./google-sign-in"
import useTranslator from "@hooks/use-translator"
import { useAuth } from "@stores/auth-store"

interface LoginFormProps extends HTMLAttributes<HTMLDivElement> {
  email?: string
}

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Please enter your email" })
    .email({ message: "Invalid email address" }),
  password: z
    .string()
    .min(1, {
      message: "Please enter your password",
    })
    .min(7, {
      message: "Password must be at least 7 characters long",
    }),
})

function LoginForm({ className, email, ...props }: LoginFormProps) {
  const [v2Fallback, setV2Fallback] = useState(false)
  const [submittedData, setSubmittedData] = useState({})
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  })

  const navigate = useNavigate()
  const { translate } = useTranslator()

  const { authConfig } = useAuth()

  const invitationKey = authConfig?.invitationKey

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || "",
      password: "",
    },
  })

  const login = async (payload: DictType) => {
    let condition = true
    if (!v2Fallback && !recaptchaToken.v3) {
      condition = false
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      })
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      condition = false
      return
    }
    if (condition) {
      const response = await axios.post(SERVER_URL + LOGIN_ENDPOINT, {
        ...payload,
        recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
        recaptcha_response_v2: payload?.v2Token,
      })

      return response.data
    }
  }

  const loginMutation = useMutation({
    mutationKey: ["login", recaptchaToken],
    mutationFn: (payload: DictType) => login(payload),
    onSuccess: (data) => {
      // persist in cookieStorage for  24hrs
      data?.authToken &&
        setCookie(null, ACCESS_TOKEN, data.authToken, {
          maxAge: 24 * 60 * 60,
          sameSite: "strict",
          path: "/",
        })

      if (data?.authToken) {
        toast({
          title: translate("Success"),
          description: translate("Login successful"),
          variant: "success",
        })
      }

      if (email) {
        // Refresh the page for previously logged in users with authDetails already set
        return navigate(0)
      }

      if (!recaptchaToken.v3 == false) {
        if (invitationKey) navigate(INVITATIONS_ACCEPT_PAGE_ROUTE)
        else navigate(LOGIN_COMPLETE_PAGE_ROUTE)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "Invalid Captcha Response. Please try again.") setV2Fallback(true)
      else {
        toast({
          title: translate("Error"),
          description: errorMessage || translate("Unable to login at this time"),
          variant: "error",
        })
      }
    },
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    // Track for v2 resubmission
    setSubmittedData(data)
    if (!v2Fallback) loginMutation.mutate(data)
  }

  const reSubmit = (token: string) => {
    loginMutation.mutate({ ...submittedData, v2Token: token })
    setV2Fallback(false)
  }

  return (
    <Card
      className='h-[500px] w-full rounded-md border-none p-6 md:w-[350px]'
      style={{
        background: "linear-gradient(blue 5.01%, rgb(232, 203, 203, 0%)",
        borderRadius: "15px 15px 0px 0px",
      }}
    >
      <div className='flex flex-col items-center justify-center space-y-2 text-left'>
        <div className='relative mt-0 p-0'>
          <img src={`${process.env.PUBLIC_URL}/group-232.svg`} alt='Brand logo' />
        </div>
      </div>

      <div className={cn("grid gap-6", className)} {...props}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='text-white'>
            <div className='flex flex-col gap-4'>
              {!email && (
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem className='flex flex-col items-start justify-start space-y-2'>
                      <FormLabel className='capitalize'>{translate("Email")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={"name@example.com"}
                          className='rounded-full text-black'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem className='flex w-full flex-col space-y-2'>
                    <FormLabel className='w-full capitalize ltr:text-left rtl:text-right'>
                      {translate("Password")}
                    </FormLabel>

                    <FormControl className=''>
                      <PasswordInput
                        placeholder='********'
                        {...field}
                        className='w-full rounded-full bg-white text-black'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                className='mt-2 rounded-full bg-primary/80 capitalize'
                loading={loginMutation.isPending}
                disabled={loginMutation.isPending || loginMutation.isSuccess || !recaptchaToken.v3}
              >
                {email ? translate("Refresh") : translate("log in")}
              </Button>

              <div className='relative my-2'>
                <div className='absolute inset-0 flex items-center'>
                  <span className='w-full border-t' />
                </div>
                <div className='relative flex justify-center text-xs uppercase'>
                  <span className='bg-background px-2 text-foreground'>
                    {translate("or continue with")}{" "}
                  </span>
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <GoogleSignIn />
              </div>
            </div>
          </form>
        </Form>
      </div>
      {!email && (
        <div className='my-3 flex items-center justify-between text-white/60'>
          <Link
            to={SIGNUP_PAGE_ROUTE}
            className='text-sm font-medium text-white/60 hover:opacity-75'
          >
            {translate("New")}? {translate("sign up here")}.
          </Link>
          <Link
            to={RESET_PASSWORD_LINK_PAGE_ROUTE}
            className='text-sm font-medium text-white/60 hover:opacity-75'
          >
            {translate("Forgot password")}?
          </Link>
        </div>
      )}

      {/* GOOGLE Recaptcha */}
      <Recaptcha setToken={setRecaptchaToken} v2={v2Fallback} reSubmit={reSubmit} />
    </Card>
  )
}

export default LoginForm
